import { SeatAssignmentModule } from './SeatAssignmentModule';

/**
 * The minimal surface of the objects `attachSeatAssignmentLifecycle` listens on,
 * so it can be driven with a fake target in tests without a DOM.
 */
export interface LifecycleTarget {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface LifecycleTargets {
  /** Usually `document`. Source of `visibilitychange`. */
  visibility?: LifecycleTarget & { visibilityState?: string };
  /** Usually `window`. Source of `pagehide` / `beforeunload`. */
  page?: LifecycleTarget;
}

/**
 * Wire a Seat Assignment module to the page lifecycle:
 *
 *  - Pending writes flush when the page is hidden and before navigation, so the
 *    most recent Seat Assignment is not lost in the coalescing window.
 *  - Assignments refetch when the tab becomes visible again, so returning from
 *    another window does not show a stale room.
 *
 * There is deliberately no background poll (ADR-0004). Returns a disposer.
 */
export function attachSeatAssignmentLifecycle(
  module: SeatAssignmentModule,
  targets?: LifecycleTargets,
): () => void {
  const defaultVisibility =
    typeof document !== 'undefined'
      ? (document as unknown as LifecycleTarget & { visibilityState?: string })
      : undefined;
  const defaultPage =
    typeof window !== 'undefined' ? (window as unknown as LifecycleTarget) : undefined;

  const visibility = targets?.visibility ?? defaultVisibility;
  const page = targets?.page ?? defaultPage;

  const onVisibilityChange = () => {
    if (visibility?.visibilityState === 'hidden') {
      void module.flushPending();
    } else {
      void module.onReconnect();
    }
  };
  const onPageHide = () => {
    void module.flushPending();
  };

  visibility?.addEventListener('visibilitychange', onVisibilityChange);
  page?.addEventListener('pagehide', onPageHide);
  page?.addEventListener('beforeunload', onPageHide);

  return () => {
    visibility?.removeEventListener('visibilitychange', onVisibilityChange);
    page?.removeEventListener('pagehide', onPageHide);
    page?.removeEventListener('beforeunload', onPageHide);
  };
}
